/**
 * Smoothing proposal card (the runtime HITL gate). Smoothing edits the
 * structure IN PLACE, like the toolbar's Smoothing panel. Pure
 * presentational: it shows the RESOLVED target, the method and level (the
 * panel's slider, 1–5), and the previewed volume change; hovering shows
 * the smoothed result. Nothing mutates until the clinician presses Apply.
 * Frame, status chip, hover preview and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import type { SmoothProposal } from '~/agent';
import { formatSignedCm3 } from '~/utils/units';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

const METHOD_LABEL: Record<SmoothProposal['method'], string> = {
    gaussian: 'Gaussian smoothing',
    surface: 'Surface smoothing',
};

export interface SmoothProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    readonly method: SmoothProposal['method'];
    readonly level: number;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function SmoothProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    method,
    level,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: SmoothProposalCardProps) {
    return (
        <ProposalCardShell
            title="Smooth structure"
            ariaLabel="Smooth structure proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                {METHOD_LABEL[method]}, level {level} of 5, on “{targetName}”
            </ProposalSummaryBox>

            <PreviewedVolumeLine
                label="Previewed volume change"
                value={formatSignedCm3(volumeDeltaCm3)}
            />

            <Typography variant="caption" color="text.secondary">
                This edits “{targetName}” itself, like the toolbar&apos;s
                Smoothing panel; no new structure is created. Undo restores it.
            </Typography>
        </ProposalCardShell>
    );
}
